import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  isSyncActive,
  syncStatusLabel,
  type SyncRun,
} from "../entities/mailboxes";
import { formatDate, useSubmissionKey } from "./counterpartiesSupport";
import { MailboxNotice } from "./MailboxNotice";
import { mailboxKeys } from "./mailboxesSupport";
import type { ShellServices } from "./services";

type Service = NonNullable<ShellServices["mailboxes"]>;

function SyncRunResult({ run }: { run: SyncRun }) {
  return (
    <div className="mb-result mb-result--ok" role="status">
      <strong>Синхронизация поставлена в очередь</strong>
      <p>
        Запуск {run.id.slice(0, 8)} · {syncStatusLabel(run.status)}
        {run.completed_at === null || run.completed_at === undefined
          ? ""
          : ` · завершён ${formatDate(run.completed_at)}`}
        . Найдено писем: {run.discovered_count}, загружено: {run.imported_count}
        .
      </p>
      <p className="muted">
        Письма, которые уже загружены, повторно не создаются: Core сверяет их по
        папке, UID и идентификатору письма.
      </p>
    </div>
  );
}

/**
 * Ручной запуск синхронизации и контрольная точка (checkpoint): до какого UID
 * Core уже нашёл письма и до какого загрузил.
 */
export function MailboxSyncPanel({
  service,
  mailboxId,
  enabled,
  canSync,
}: {
  service: Service;
  mailboxId: string;
  enabled: boolean;
  canSync: boolean;
}) {
  const client = useQueryClient();
  const key = useSubmissionKey();
  const [run, setRun] = useState<SyncRun | null>(null);

  const state = useQuery({
    queryKey: mailboxKeys.syncState(mailboxId),
    queryFn: () => service.syncState(mailboxId),
    // Пока Core разбирает очередь, состояние перечитывается само.
    refetchInterval: (query) =>
      query.state.data !== undefined && isSyncActive(query.state.data.status)
        ? 5000
        : false,
    retry: false,
  });

  const sync = useMutation({
    // Ключ живёт, пока запуск не удался: повтор после сбоя сети уйдёт с тем же
    // ключом и не создаст второй запуск. После успеха следующий запуск — новое
    // намерение и получит новый ключ.
    mutationFn: () => service.sync(mailboxId, key.current()),
    onSuccess: async (created) => {
      key.reset();
      setRun(created);
      await Promise.all([
        client.invalidateQueries({
          queryKey: mailboxKeys.syncState(mailboxId),
        }),
        client.invalidateQueries({ queryKey: mailboxKeys.detail(mailboxId) }),
      ]);
    },
  });

  const data = state.data;

  return (
    <section className="cp-section mb-sync" aria-labelledby="mb-sync-title">
      <div className="mb-section-head">
        <h3 id="mb-sync-title">Синхронизация</h3>
        {canSync ? (
          <button
            type="button"
            className="button button--secondary button--compact"
            disabled={sync.isPending || !enabled}
            onClick={() => sync.mutate()}
          >
            {sync.isPending ? "Запускаем…" : "Запустить синхронизацию"}
          </button>
        ) : null}
      </div>
      {!canSync ? (
        <p className="muted">
          Запускать синхронизацию может сотрудник с правом «Синхронизация
          почты».
        </p>
      ) : null}
      {canSync && !enabled ? (
        <p className="muted">
          Ящик отключён: включите его, чтобы запустить синхронизацию.
        </p>
      ) : null}
      {sync.isError ? (
        <MailboxNotice
          error={sync.error}
          fallback="Не удалось запустить синхронизацию."
          onRetry={() => sync.mutate()}
          retrying={sync.isPending}
        />
      ) : null}
      {run === null ? null : <SyncRunResult run={run} />}

      {state.isLoading ? <p aria-busy="true">Читаем состояние…</p> : null}
      {state.isError ? (
        <MailboxNotice
          error={state.error}
          fallback="Не удалось прочитать состояние синхронизации."
          onRetry={() => void state.refetch()}
          retrying={state.isFetching}
        />
      ) : null}
      {data === undefined ? null : (
        <dl
          className="cp-requisites mb-checkpoint"
          aria-label="Контрольная точка"
        >
          <div>
            <dt>Состояние</dt>
            <dd>{syncStatusLabel(data.status)}</dd>
          </div>
          <div>
            <dt>Папка</dt>
            <dd>{data.folder}</dd>
          </div>
          <div>
            <dt>Найдено до UID</dt>
            <dd>{data.discovery_uid}</dd>
          </div>
          <div>
            <dt>Загружено до UID</dt>
            <dd>{data.imported_uid}</dd>
          </div>
          <div>
            <dt>UIDVALIDITY</dt>
            <dd>{data.uidvalidity ?? "—"}</dd>
          </div>
          <div>
            <dt>Последний запуск</dt>
            <dd>{formatDate(data.last_started_at)}</dd>
          </div>
          <div>
            <dt>Последнее завершение</dt>
            <dd>{formatDate(data.last_completed_at)}</dd>
          </div>
        </dl>
      )}
    </section>
  );
}
